import { Component, Vue } from 'vue-property-decorator';
import { PaymentMethod, Stripe, StripeCardElement, StripeIdealBankElement, loadStripe } from '@stripe/stripe-js';
import { ValidationObserver } from 'vee-validate';
import { CurrencyDirective, parse } from 'vue-currency-input';
import template from './Balance.vue';

import Button from '@/components/Button';
import BackButton from '@/components/BackButton';
import MDTextFieldWithValidation from '@/components/Validation/MDTextFieldWithValidation';
import payment from '@/store/modules/payment';
import store from '@/store';
import { Meta } from '@/store/models';
import auth from '@/store/modules/auth';
import { PaymentType } from '@/store/models/payment';
import { ErrorFrontend, validation } from '@/_helpers/validation';
import { router } from '@/_helpers';
import { env } from '@/_helpers/env';

@Component({
	mixins: [template],
	components: {
		ValidationObserver,
		Button,
		BackButton,
		MDTextFieldWithValidation
	},
	directives: {
		currency: CurrencyDirective
	},
	filters: {
		money(amount: number): string {
			let afterDigits: string = `${amount % 100}`;
			if (amount % 100 === 0) {
				afterDigits = '-';
			} else if (amount % 100 < 10) {
				afterDigits = `0${afterDigits}`;
			}
			return `€${amount / 100},${afterDigits}`;
		}
	}
})
export default class Balance extends Vue {
	loading: boolean = false;

	disableAmount: boolean = false;

	setting: PaymentType = PaymentType.NONE;

	balance: number = 0;

	amount: string = '';

	stripe: Stripe | null = null;

	cardElement: StripeCardElement | null = null;

	idealElement: StripeIdealBankElement | null = null;

	choosenElement: PaymentMethod | null = null;

	addBalance: boolean = false;

	PaymentIntentSecret: string = '';

	form: any = {
		billingDetails: {
			name: ''
		}
	};

	options: any = {
		autoDecimalMode: true,
		valueAsInteger: true,
		allowNegative: false
	};

	alertShow: boolean = false;

	alertTxt: string = '';

	ccErrors: ErrorFrontend = {
		cc_validation: []
	};

	async mounted(): Promise<void> {
		this.loading = true;

		await auth.getSelf(false);
		await this.getBalance();

		this.loading = false;

		// LOAD stripe
		if (env.STRIPE_KEY !== undefined) {
			this.stripe = await loadStripe(env.STRIPE_KEY);
		}

		const elements = this.stripe?.elements();
		if (elements !== undefined) {
			this.cardElement = elements.create('card', {
				hidePostalCode: true
			});
			this.cardElement.on('change', (event) => {
				this.ccErrors.cc_validation = [];
				if (event.error) {
					this.ccErrors.cc_validation.push(event.error.message);
				}
				(this.$refs.form as any).setErrors(this.ccErrors);
			});
			this.cardElement.mount('#card-element');

			this.idealElement = elements.create('idealBank', {
				value: 'rabobank'
			});
			this.idealElement.mount('#ideal-element');
		}
	}

	get meta(): Meta | null {
		return payment.meta ? payment.meta : null;
	}

	async getBalance(): Promise<void> {
		this.loading = true;
		try {
			await payment.getBalance(auth.self.customerId);
			this.balance = payment.balance;
		} catch (error) {
			store.commit('openNotifier', {
				text: `Er ging wat fout tijdens het ophalen van het balans`,
				color: 'error'
			});
		}
		this.loading = false;
	}

	setSetting(setting: PaymentType): void {
		this.alertShow = false;
		this.alertTxt = '';
		this.setting = setting;
	}

	async startAddBalance(): Promise<void> {
		const isValid = await (this.$refs.formAmount as any).validate();
		if (!isValid) {
			return;
		}
		const amount = parse(this.amount, this.options);
		if (!amount) {
			return;
		}
		this.disableAmount = true;
		this.loading = true;

		try {
			await payment.createPaymentIntent({
				id: auth.self.customerId,
				amount
			});
			this.PaymentIntentSecret = payment.paymentIntent;
			this.addBalance = true;
		} catch (err) {
			(this.$refs.formAmount as any).setErrors(validation.getErrors(err.data.errors));
		} finally {
			this.loading = false;
		}
		this.setSetting(PaymentType.IDEAL); // Set default to ideal
	}

	async add(): Promise<void> {
		const isValid = await (this.$refs.form as any).validate();

		if (this.setting === PaymentType.CARD) {
			// Is necessary to force the stripe errors to not been thrown away
			(this.$refs.form as any).setErrors(this.ccErrors);
		}

		if (!isValid) {
			return;
		}

		this.loading = true;
		this.alertShow = false;
		this.alertTxt = '';

		if (this.stripe !== null) {
			if (this.setting === PaymentType.CARD && this.cardElement !== null) {
				const { paymentIntent, error } = await this.stripe.confirmCardPayment(this.PaymentIntentSecret, {
					payment_method: {
						card: this.cardElement,
						billing_details: {
							name: this.form.billingDetails.name
						}
					}
				});

				if (error) {
					// TODO
					// Add conversion list from stripe code to text
					// Add html to bind the error on
					if (error.message !== undefined) {
						this.alertShow = true;
						this.alertTxt = error.message;
					}
				} else {
					router.push({ name: 'payment' });
				}
			} else if (this.setting === PaymentType.IDEAL && this.idealElement !== null) {
				// Get domain
				const url = window.location.href;
				const arr = url.split('/');
				const link = `${arr[0]}//${arr[2]}`;

				const { paymentIntent, error } = await this.stripe.confirmIdealPayment(this.PaymentIntentSecret, {
					payment_method: {
						ideal: this.idealElement
					},
					return_url: `${link}/betaling`
				});
			}
		}
		this.loading = false;
	}
}
